import { createWorkspaceAuthProvider } from '@secureblob/sdk'
import { recordBrowserHttpErrorResponse } from '@secureblob/sdk/browserHttpErrors'
import type {
    CallHandlerOptions,
    ComponentJobsClientOptions,
    ComponentJobsFetch,
    ComponentHandlerCallResult,
    ComponentJobServerRunner,
    ComponentRunnerBootstrap,
} from '@secureblob/sdk/componentJobs'
import { callHandler as callComponentJobsHandler } from '@secureblob/sdk/componentJobs'
import type { WorkspaceEntry } from '../../engine/types'
import { recordComponentJobRunProbeNetworkFailure } from './runProbe'

const CRYPTO_BLOBSTORE_SUFFIX = '/api/cryptoblobstore'
const COMPONENT_JOBS_SUFFIX = '/api/components'

type ComponentJobsWorkspaceEntry = Pick<WorkspaceEntry, 'apiBaseUrl' | 'blobId' | 'capability'>

function resolveFetchInputUrl(input: RequestInfo | URL): string | undefined {
    if (typeof input === 'string') {
        return input
    }
    if (input instanceof URL) {
        return input.toString()
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.url
    }
    return undefined
}

function resolveFetchMethod(input: RequestInfo | URL, method?: string): string | undefined {
    if (method?.trim()) {
        return method
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.method
    }
    return undefined
}

export function resolveComponentJobsApiBaseUrl(apiBaseUrl: string): string {
    const trimmed = apiBaseUrl.trim().replace(/\/+$/, '')
    if (trimmed.endsWith(COMPONENT_JOBS_SUFFIX)) {
        return trimmed
    }
    if (trimmed.endsWith(CRYPTO_BLOBSTORE_SUFFIX)) {
        return `${trimmed.slice(0, -CRYPTO_BLOBSTORE_SUFFIX.length)}${COMPONENT_JOBS_SUFFIX}`
    }
    if (trimmed.endsWith('/api')) {
        return `${trimmed}/components`
    }
    return `${trimmed}${COMPONENT_JOBS_SUFFIX}`
}

export function createComponentJobsClientOptions(entry: ComponentJobsWorkspaceEntry): ComponentJobsClientOptions {
    return {
        apiBaseUrl: resolveComponentJobsApiBaseUrl(entry.apiBaseUrl),
        fetch: createAuthenticatedComponentJobsFetch(entry),
    }
}

export function createComponentRunnerBootstrap(
    entry: ComponentJobsWorkspaceEntry,
    serverRunner?: ComponentJobServerRunner | null,
): ComponentRunnerBootstrap {
    return {
        apiBaseUrl: entry.apiBaseUrl.trim().replace(/\/+$/, ''),
        workspaceBlobId: entry.blobId,
        ...(serverRunner?.connectionCredential
            ? { configConnectionToken: serverRunner.connectionCredential }
            : { workspaceSecret: entry.capability }),
        localStorePolicy: 'memory',
    }
}

export function createComponentSourceSnapshot(
    workspaceBlobId: string,
    sourcePath: string,
    sourceMtime: number,
): string {
    return [
        'workspace-head',
        workspaceBlobId,
        sourcePath,
        String(Math.max(0, sourceMtime)),
    ].join(':')
}

export async function callHandler(
    options: CallHandlerOptions,
): Promise<ComponentHandlerCallResult> {
    return callComponentJobsHandler(options)
}

export type { CallHandlerOptions, ComponentHandlerCallResult }

function createAuthenticatedComponentJobsFetch(entry: ComponentJobsWorkspaceEntry): ComponentJobsFetch {
    const authProvider = createWorkspaceAuthProvider(entry.apiBaseUrl, entry.capability, entry.blobId)
    const fetchImpl = globalThis.fetch?.bind(globalThis)
    if (!fetchImpl) {
        throw new Error('Component jobs client requires a global fetch implementation.')
    }

    return async (input, init) => {
        const scope = resolveRequestScope(init?.method)
        const initialHeaders = new Headers(init?.headers)
        const hadAuthorization = initialHeaders.has('authorization')

        const perform = async (retrying: boolean): Promise<Response> => {
            const headers = new Headers(initialHeaders)

            if (!hadAuthorization) {
                if (authProvider) {
                    const token = await authProvider.getAccessToken(scope)
                    headers.set('authorization', `Bearer ${token.token}`)
                } else {
                    headers.set('authorization', `Bearer ${entry.capability}`)
                }
            }

            let response: Response
            try {
                response = await fetchImpl(input, { ...init, headers })
            } catch (error) {
                recordComponentJobRunProbeNetworkFailure({
                    url: resolveFetchInputUrl(input) ?? '(unknown)',
                    method: resolveFetchMethod(input, init?.method),
                    errorText: error instanceof Error ? error.message : String(error),
                })
                throw error
            }
            if (response.status === 401 && authProvider && !hadAuthorization && !retrying) {
                authProvider.invalidateToken(scope)
                return perform(true)
            }

            recordBrowserHttpErrorResponse(response, {
                url: resolveFetchInputUrl(input),
                method: resolveFetchMethod(input, init?.method),
            })

            return response
        }

        return perform(false)
    }
}

function resolveRequestScope(method: string | undefined): 'read' | 'write' {
    switch (method?.trim().toUpperCase()) {
        case undefined:
        case '':
        case 'GET':
        case 'HEAD':
        case 'OPTIONS':
            return 'read'
        default:
            return 'write'
    }
}
